import {
  DndContextProps,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { SortableContextProps, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useTableProps, useTable } from "@refinedev/antd";
import { BaseRecord, HttpError } from "@refinedev/core";
import { SortableRow } from "./sortableRow";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { TableProps } from "antd";
import { useCallback, useEffect, useState } from "react";
import { isMobileDevice } from "utils/deviceUtils";

export type UseStorableTableProps<
  TQueryFnData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TSearchVariables = unknown,
  TData extends BaseRecord = TQueryFnData
> = useTableProps<TQueryFnData, TError, TSearchVariables, TData> & {
  rowKey: string;
};

export const useSortableTable = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TSearchVariables = unknown,
  TData extends BaseRecord = TQueryFnData
>({
  rowKey,
  ...props
}: UseStorableTableProps<TQueryFnData, TError, TSearchVariables, TData>) => {
  const table = useTable<TQueryFnData, TError, TSearchVariables, TData>(props);
  const originalDataSource = table.tableProps.dataSource as TData[] | undefined;
  const [dataSource, setDataSource] = useState(originalDataSource);
  const [isReordered, setIsReordered] = useState(false);
  const sensors = useSensors(
    useSensor(isMobileDevice() ? TouchSensor : PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1
      }
    })
  );

  // handle drag end
  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        setDataSource((prev) => {
          if (prev) {
            const activeIndex = prev.findIndex((it) => it[rowKey] === active.id);
            const overIndex = prev.findIndex((it) => it[rowKey] === over?.id);
            const next = arrayMove(prev, activeIndex, overIndex);

            if (next.some((it, i) => it[rowKey] !== prev[i][rowKey])) {
              setIsReordered(true);
            }

            return next;
          }
        });
      }
    },
    [rowKey]
  );

  // update data source when original data source changed
  useEffect(() => {
    setDataSource(originalDataSource);
    setIsReordered(false);
  }, [originalDataSource]);

  return {
    ...table,
    dndContextProps: {
      sensors,
      modifiers: [restrictToVerticalAxis],
      onDragEnd
    } satisfies Omit<DndContextProps, "children">,
    sortableContextProps: {
      items: dataSource?.map((it) => (it as Record<string, UniqueIdentifier>)[rowKey]) ?? [],
      strategy: verticalListSortingStrategy
    } satisfies Omit<SortableContextProps, "children">,
    tableProps: {
      ...table.tableProps,
      components: {
        ...table.tableProps.components,
        body: {
          ...table.tableProps.components?.body,
          row: SortableRow
        }
      },
      style: {
        position: "relative",
        zIndex: 0, // prevent empty message from appearing over modals
        touchAction: "none"
      },
      rowKey,
      dataSource
    } satisfies TableProps<TData>,

    // indicates if data source is reordered, should be used to update the remote order
    isReordered
  };
};
